import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Button, 
  TextField, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Paper, 
  CircularProgress, 
  useTheme, 
  Drawer, 
  IconButton, 
  useMediaQuery, 
  Divider 
} from '@mui/material';
import { fetchOpenOrders, cancelSpotTrade } from '../../store/Slices/spotTradingSlice';
import CloseIcon from '@mui/icons-material/Close';

const OpenOrders = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const { openOrders, openOrdersLoading, error } = useSelector((state) => state.spotTrading);
  const themeMode = useSelector((state) => state.theme.light ? 'light' : 'dark');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tradeTypeFilter, setTradeTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!user?._id) {
      window.location.href = '/login';
      return;
    }

    if (openOrders.length > 0) {
      console.log('Using persisted openOrders');
      return;
    }

    dispatch(fetchOpenOrders(user._id)).unwrap().catch((err) => {
      if (err === 'Unauthorized') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    });
  }, [dispatch, user?._id, openOrders.length]);

  const handleCancel = (tradeId) => {
    dispatch(cancelSpotTrade(tradeId)).unwrap().catch((err) => {
      if (err === 'Unauthorized') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    });
  };

  const filteredOrders = openOrders.filter((order) => {
    const matchesTradeType = tradeTypeFilter === 'All' || order.trade_type === tradeTypeFilter;
    const matchesStatus = statusFilter === 'All' || order.status === statusFilter;
    const matchesSymbol = symbolFilter === '' || (order.trading_pair_id?.symbol || '').toLowerCase().includes(symbolFilter.toLowerCase());
    return matchesTradeType && matchesStatus && matchesSymbol;
  });

  const tradeTypes = ['All', ...new Set(openOrders.map((order) => order.trade_type).filter(Boolean))];
  const statuses = ['All', ...new Set(openOrders.map((order) => order.status).filter(Boolean))];

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setDrawerOpen(true);
  };

  const handleRetry = () => {
    if (user?._id) {
      dispatch(fetchOpenOrders(user._id)).unwrap().catch((err) => {
        if (err === 'Unauthorized') {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      });
    }
  };

  if (!user?._id) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography sx={{ color: theme.palette.error.main, mb: 2 }}>
          Please log in to view open orders.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => (window.location.href = '/login')}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  if (openOrdersLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress sx={{ color: theme.palette.brand.main }} />
        <Typography sx={{ mt: 2, color: theme.palette.text.secondary }}>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography sx={{ color: theme.palette.error.main, mb: 2 }}>
          Error: {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetry}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.palette.mode === 'dark' ? '0 2px 10px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography variant="h6" sx={{ mb: 3, color: theme.palette.text.primary, fontWeight: 600 }}>
        Open Orders
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
        <FormControl sx={{ minWidth: isMobile ? '100%' : 120 }}>
          <InputLabel sx={{ color: theme.palette.text.secondary }}>Trade Type</InputLabel>
          <Select
            value={tradeTypeFilter}
            onChange={(e) => setTradeTypeFilter(e.target.value)}
            label="Trade Type"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#F5F5F5',
              color: theme.palette.text.primary,
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.brand.main },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.brand.main },
            }}
          >
            {tradeTypes.map((type) => (
              <MenuItem key={type} value={type} sx={{ color: theme.palette.text.primary }}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: isMobile ? '100%' : 120 }}>
          <InputLabel sx={{ color: theme.palette.text.secondary }}>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#F5F5F5',
              color: theme.palette.text.primary,
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.brand.main },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.brand.main },
            }}
          >
            {statuses.map((status) => (
              <MenuItem key={status} value={status} sx={{ color: theme.palette.text.primary }}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Search by Symbol"
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          sx={{
            minWidth: isMobile ? '100%' : 200,
            '& .MuiInputBase-root': { bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#F5F5F5', borderRadius: 1 },
            '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0' },
              '&:hover fieldset': { borderColor: theme.palette.brand.main },
              '&.Mui-focused fieldset': { borderColor: theme.palette.brand.main },
            },
          }}
        />
      </Box>

      {filteredOrders.length === 0 ? (
        <Typography sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 4 }}>
          No open orders currently.
        </Typography>
      ) : (
        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table sx={{ minWidth: isMobile ? '100%' : 650, border: `1px solid ${theme.palette.divider}` }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1A1C22' : '#F0F0F0' }}>
                <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>Pair</TableCell>
                <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>Amount</TableCell>
                {!isMobile && (
                  <>
                    <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>Limit Price</TableCell>
                    <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: theme.palette.brand.main, fontWeight: 'bold' }}>Action</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow 
                  key={order._id} 
                  onClick={() => isMobile && handleRowClick(order)} 
                  sx={{ 
                    '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F5F5F5', cursor: isMobile ? 'pointer' : 'default' },
                  }}
                >
                  <TableCell sx={{ color: theme.palette.text.primary }}>{order.trading_pair_id?.symbol || 'N/A'}</TableCell>
                  <TableCell sx={{ color: order.trade_type === 'Buy' ? theme.palette.success.main : theme.palette.error.main }}>
                    {order.trade_type}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{order.amount.toFixed(4)}</TableCell>
                  {!isMobile && (
                    <>
                      <TableCell sx={{ color: theme.palette.text.primary }}>{order.limit_price ? order.limit_price.toFixed(2) : '-'}</TableCell>
                      <TableCell sx={{ color: order.status === 'Pending' ? theme.palette.brand.main : theme.palette.text.secondary }}>
                        {order.status}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancel(order._id);
                          }}
                          disabled={order.status !== 'Pending'}
                          sx={{
                            bgcolor: theme.palette.error.main,
                            color: '#FFFFFF',
                            '&:hover': { bgcolor: '#B71C1C' },
                            '&:disabled': { bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0', color: theme.palette.text.secondary },
                            borderRadius: 1,
                            textTransform: 'none',
                            px: 2,
                          }}
                        >
                          Cancel
                        </Button>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            p: 2,
            maxHeight: '80vh',
          },
        }}
      >
        {selectedOrder && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                Order Details
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon sx={{ color: theme.palette.text.secondary }} />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2, bgcolor: theme.palette.divider }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                  Pair
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {selectedOrder.trading_pair_id?.symbol || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                  Type
                </Typography>
                <Typography sx={{ color: selectedOrder.trade_type === 'Buy' ? theme.palette.success.main : theme.palette.error.main }}>
                  {selectedOrder.trade_type}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                  Amount
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {selectedOrder.amount.toFixed(4)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                  Limit Price
                </Typography>
                <Typography sx={{ color: theme.palette.text.primary }}>
                  {selectedOrder.limit_price ? selectedOrder.limit_price.toFixed(2) : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                  Status
                </Typography>
                <Typography sx={{ color: selectedOrder.status === 'Pending' ? theme.palette.brand.main : theme.palette.text.secondary }}>
                  {selectedOrder.status}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                  Action
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleCancel(selectedOrder._id);
                    setDrawerOpen(false);
                  }}
                  disabled={selectedOrder.status !== 'Pending'}
                  sx={{
                    bgcolor: theme.palette.error.main,
                    color: '#FFFFFF',
                    '&:hover': { bgcolor: '#B71C1C' },
                    '&:disabled': { bgcolor: theme.palette.mode === 'dark' ? '#2A2E37' : '#E0E0E0', color: theme.palette.text.secondary },
                    borderRadius: 1,
                    textTransform: 'none',
                    px: 2,
                    mt: 1,
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </Paper>
  );
};

export default OpenOrders;